import React, { useState, useEffect } from 'react';
import { StarIcon } from '../icons';
import { formatDateSafe } from '../../utils/helpers';
import { normalizeDestination } from '../../utils/destinations';
import QuestionnaireStepper from './QuestionnaireStepper';
import CompanySubmissionHistory from './CompanySubmissionHistory';

const CompanyDashboard = ({ currentUser, currentMonth, resolvedDeadline, onNotify, onSubmit, submissions, canSubmit }) => {
  const [editingSubmission, setEditingSubmission] = useState(null);

  useEffect(() => {
    if (editingSubmission) {
      const fresh = submissions.find(s => s.docId === editingSubmission.docId);
      if (!fresh || fresh.status !== 'rejected') {
        setEditingSubmission(null);
      }
    }
  }, [submissions, editingSubmission]);

  const orderedSubmissions = [...submissions].sort((a, b) => {
    const dateA = new Date(a.submittedAt || a.resubmittedAt || 0);
    const dateB = new Date(b.submittedAt || b.resubmittedAt || 0);
    return dateB - dateA;
  });

  const formatDate = (iso) => {
    return formatDateSafe(iso, 'Sin definir');
  };

  const deadlineCopy = resolvedDeadline ? `Tienes plazo hasta el ${formatDate(resolvedDeadline)} para enviar el cuestionario de este mes.` : 'Aún no se ha definido un plazo para este período.';
  const lastSubmission = orderedSubmissions[0];
  const now = new Date();
  const localePeriod = now.toLocaleDateString('es-EC', { month: 'long', year: 'numeric' });
  const formattedPeriod = localePeriod.charAt(0).toUpperCase() + localePeriod.slice(1);
  const deadlineDisplay = resolvedDeadline ? formatDate(resolvedDeadline) : 'Por confirmar';
  const statusLabel = canSubmit ? 'Envío pendiente' : 'Revisión en curso';
  const statusBadgeClass = canSubmit ? 'bg-emerald-100 border border-emerald-200 text-emerald-700' : 'bg-amber-100 border border-amber-200 text-amber-700';
  const statusHelper = canSubmit ? 'Completa el cuestionario para mantener activa tu participación.' : 'Tu envío más reciente está siendo revisado. Te notificaremos si requiere ajustes.';
  const quickStats = [
    { label: 'Periodo', value: formattedPeriod },
    { label: 'Fecha límite', value: deadlineDisplay },
    { label: 'Último envío', value: lastSubmission ? (lastSubmission.submittedAt || lastSubmission.resubmittedAt ? formatDate(lastSubmission.submittedAt || lastSubmission.resubmittedAt) : lastSubmission.month) : 'Sin envíos' },
  ];
  return (
    <div className="min-h-screen bg-brand-surface">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6 md:px-6 md:py-8">
        <div className="grid lg:grid-cols-[1.6fr_0.8fr] gap-6">
          <div className="card-premium overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-[#004AAD] to-[#3B82F6]" />
            <div className="p-6 space-y-6 md:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400">Panel exportador</p>
                  <h1 className="text-2xl font-bold font-jakarta mt-1 text-slate-900 sm:text-3xl">Hola, {currentUser.name}</h1>
                  <p className="text-slate-500">Sector {currentUser.exportType}</p>
                </div>
                <div className="sm:text-right">
                  <p className="text-[0.6rem] uppercase text-slate-400">Estrellas</p>
                  <p className="text-4xl font-bold font-jakarta flex items-center gap-2 sm:justify-end text-brand-blue">
                    {currentUser.stars}
                    <StarIcon solid className="h-7 w-7 text-amber-400" />
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { label: 'Último envío', value: lastSubmission ? (lastSubmission.submittedAt || lastSubmission.resubmittedAt ? formatDate(lastSubmission.submittedAt || lastSubmission.resubmittedAt) : lastSubmission.month) : 'Sin envíos' },
                  { label: 'Destino principal', value: normalizeDestination(currentUser.destination) },
                  { label: 'Contacto', value: currentUser.contactEmail || 'Sin registrar' },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-xl border border-slate-100 bg-slate-50/80 p-4 transition-all duration-200 hover:shadow-sm hover:border-slate-200">
                    <p className="text-[0.6rem] uppercase text-slate-400">{stat.label}</p>
                    <p className="text-base font-semibold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="card-premium p-6 space-y-4">
            <p className="text-[0.6rem] uppercase text-slate-400">Fecha límite</p>
            <p className="text-3xl font-bold font-jakarta text-slate-900">{resolvedDeadline ? formatDate(resolvedDeadline) : 'Por confirmar'}</p>
            <p className="text-slate-600">{deadlineCopy}</p>
            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              Programamos un recordatorio dos días antes del cierre, a las 09:00 de Ecuador. Si necesitas una extensión, solicítala desde tu perfil.
            </div>
          </div>
        </div>

        <section className="card-premium overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex flex-wrap items-start justify-between gap-4 md:px-8">
            <div>
              <p className="text-[0.6rem] uppercase text-slate-400">Llenar cuestionario</p>
              <h2 className="text-2xl font-semibold text-slate-900 mt-0.5">Envía tu pulso mensual</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {quickStats.map((stat) => (
                  <span
                    key={stat.label}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600"
                  >
                    {stat.label}: {stat.value}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusBadgeClass}`}>
                {statusLabel}
              </span>
              <p className="text-sm text-slate-500">Periodo {formattedPeriod}</p>
            </div>
          </div>
          <div className="px-6 pt-5 text-sm text-slate-600 space-y-1 md:px-8">
            <p>{statusHelper}</p>
            <p className="text-slate-500">{deadlineCopy}</p>
          </div>
          <div className="px-6 pb-8 text-sm text-slate-500 md:px-8 md:pb-10">
            <QuestionnaireStepper
              currentUser={currentUser}
              currentMonth={currentMonth}
              deadline={resolvedDeadline}
              onNotify={onNotify}
              onSubmit={onSubmit}
              existingSubmission={editingSubmission}
              onCancelEdit={() => setEditingSubmission(null)}
              canSubmit={canSubmit}
            />
          </div>
        </section>

        <CompanySubmissionHistory 
          submissions={orderedSubmissions} 
          onEdit={setEditingSubmission}
          editingSubmissionId={editingSubmission?.docId}
        />
      </div>
    </div>
  );
};

export default CompanyDashboard;
